import {Injectable} from '@angular/core';

let scrollLockKeyCounter = 0;

export function createScrollLockKey(prefix: string): string {
    return `${prefix}-${++scrollLockKeyCounter}`;
}

@Injectable({
    providedIn: 'root'
})
export class DomService {

    private scrollbarWidth: number | null = null;
    private scrollLockKeys = new Set<string>();

    lockBodyScroll(key: string): void {
        const wasEmpty = this.scrollLockKeys.size === 0;
        this.scrollLockKeys.add(key);
        if (wasEmpty) {
            this.applyScrollLock();
        }
    }

    unlockBodyScroll(key: string): void {
        if (!this.scrollLockKeys.delete(key)) {
            return;
        }
        if (this.scrollLockKeys.size === 0) {
            this.applyScrollRestore();
        }
    }

    private applyScrollLock(): void {
        const body = document.body;
        const hasVerticalScrollbar = document.documentElement.scrollHeight > document.documentElement.clientHeight;
        if (this.scrollbarWidth === null) {
            this.scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        }

        body.style.overflow = 'hidden';
        if (hasVerticalScrollbar && this.scrollbarWidth > 0) {
            body.style.paddingRight = `${this.scrollbarWidth}px`;
        }
    }

    private applyScrollRestore(): void {
        const body = document.body;

        body.style.overflow = '';
        body.style.paddingRight = '';
    }

    scrollToElement(nativeElement: Element, elementName: string, block: ScrollLogicalPosition = 'start', delay = 0): void {
        const element = nativeElement.getElementsByClassName(elementName);
        if (element && element.length > 0) {
            const scroll = () => element[0].scrollIntoView({ behavior: 'smooth', block, inline: 'nearest' });
            delay > 0 ? setTimeout(scroll, delay) : scroll();
        }
    }

    /**
     * Scrolls the active item into view within its nearest scrollable ancestor only.
     * Unlike scrollToElement (which uses scrollIntoView and can scroll the whole page),
     * this never touches the window scroll — used for in-dropdown keyboard navigation.
     */
    scrollActiveIntoContainer(root: Element, elementName: string): void {
        const item = root.querySelector('.' + elementName) as HTMLElement | null;
        if (!item) {
            return;
        }
        const container = this.findScrollableParent(item);
        if (!container) {
            return;
        }
        const containerRect = container.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        if (itemRect.top < containerRect.top) {
            container.scrollTop -= containerRect.top - itemRect.top;
        } else if (itemRect.bottom > containerRect.bottom) {
            container.scrollTop += itemRect.bottom - containerRect.bottom;
        }
    }

    private findScrollableParent(element: HTMLElement): HTMLElement | null {
        let parent = element.parentElement;
        while (parent) {
            const overflowY = window.getComputedStyle(parent).overflowY;
            const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
            if (isScrollable && parent.scrollHeight > parent.clientHeight) {
                return parent;
            }
            parent = parent.parentElement;
        }
        return null;
    }
}
